import type { AxiosResponse, AxiosError } from 'axios';
import { normalizeApiError } from '@api/error-normalizer';
import { useAppStore } from '@store/app.store';
import { logger } from '@utils/logger.util';

const HTTP_UNAUTHORIZED = 401;
const API_ERROR_BYPASS_LIST = [
  'offers',
  'skywards',
  'AddFFP',
  'SpecialService',
  'Offload',
  'namecheck',
  'validategroup',
] as const;
interface ResolvedCmsError {
  cmsKey: string | null;
  message: string | null;
}

const resolveCmsError = (details: Record<string, unknown> | undefined): ResolvedCmsError => {
  const errors = details?.errors;

  if (!Array.isArray(errors) || errors.length === 0) {
    return { cmsKey: null, message: null };
  }

  const [firstError] = errors as unknown[];

  if (!firstError || typeof firstError !== 'object') {
    return { cmsKey: null, message: null };
  }

  const errorRecord = firstError as Record<string, unknown>;
  const cmsKey = errorRecord.cmskey;
  const message = errorRecord.message;

  return {
    cmsKey: typeof cmsKey === 'string' ? cmsKey : null,
    message: typeof message === 'string' ? message : null,
  };
};

export const responseInterceptor = <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
  return response;
};

export const responseErrorInterceptor = (error: AxiosError): Promise<never> => {
  const normalized = normalizeApiError(error);
  const { cmsKey, message } = resolveCmsError(normalized.details);
  logger.error('API Error', normalized);
  const requestUrl = error.response?.config.url ?? error.config?.url ?? '';
  const isBypass = API_ERROR_BYPASS_LIST.some((item) => requestUrl.includes(item));

  if (cmsKey && !isBypass) {
    useAppStore.getState().setLoadingError({
      cmsKey,
      hasError: true,
      errorMessage: message ?? normalized.message,
    });
  }

  if (error.response?.status === HTTP_UNAUTHORIZED) {
    handleUnauthorized();
  }

  return Promise.reject(normalized);
};

const handleUnauthorized = (): void => {
  sessionStorage.clear();
  window.location.href = '/';
};
